import { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import { api, API } from "@/lib/apiClient";

const RadioCtx = createContext(null);
export const useRadio = () => useContext(RadioCtx);

export function getPool(cfg) {
  let pool = cfg?.tracks || [];
  if (cfg?.schedule_enabled && cfg.active_block) {
    const f = pool.filter((t) => (t.category || "general") === cfg.active_block.category);
    if (f.length) pool = f;
  }
  return pool;
}

export function computePosition(cfg) {
  const tracks = getPool(cfg);
  const total = tracks.reduce((s, t) => s + (t.duration || 0), 0);
  if (!total) return null;
  let elapsed = (Date.now() / 1000 + (cfg._offset || 0) - cfg.anchor) % total;
  for (let i = 0; i < tracks.length; i++) {
    if (elapsed < tracks[i].duration) return { idx: i, offset: elapsed };
    elapsed -= tracks[i].duration;
  }
  return { idx: 0, offset: 0 };
}

export function RadioProvider({ children }) {
  const [config, setConfig] = useState(null);
  const [playing, setPlaying] = useState(false);
  const [loading, setLoading] = useState(false);
  const [nowTitle, setNowTitle] = useState("");
  const [upNext, setUpNext] = useState("");
  const audioRef = useRef(null);
  const cfgRef = useRef(null);

  useEffect(() => {
    refresh();
    const t = setInterval(refresh, 60000);
    return () => clearInterval(t);
  }, []);

  const refresh = async () => {
    try {
      const r = await api.get("/radio/state");
      const cfg = { ...r.data, _offset: r.data.server_time - Date.now() / 1000 };
      cfgRef.current = cfg;
      setConfig(cfg);
      return cfg;
    } catch { return cfgRef.current; }
  };

  const startLive = useCallback(() => {
    const cfg = cfgRef.current;
    if (!cfg) return;
    refresh();
    let audio = audioRef.current;
    if (!audio) { audio = new Audio(); audio.preload = "auto"; audioRef.current = audio; }
    if (cfg.mode === "external" && cfg.external_url) {
      setNowTitle(cfg.external_label || cfg.station_name || "Live Stream");
      setUpNext("");
      setLoading(true);
      audio.src = cfg.external_url;
      audio.onended = null;
      audio.onloadedmetadata = null;
      audio.play().then(() => { setPlaying(true); setLoading(false); }).catch(() => { setPlaying(false); setLoading(false); });
      return;
    }
    const pos = computePosition(cfg);
    if (!pos) return;
    const pool = getPool(cfg);
    const track = pool[pos.idx];
    const next = pool[(pos.idx + 1) % pool.length];
    setNowTitle(track.title);
    setUpNext(pool.length > 1 ? next.title : "");
    setLoading(true);
    audio.src = `${API}/radio/audio/${track.id}`;
    audio.onloadedmetadata = () => { try { audio.currentTime = pos.offset; } catch {} };
    audio.onended = async () => { await refresh(); startLive(); };
    audio.play().then(() => { setPlaying(true); setLoading(false); }).catch(() => { setPlaying(false); setLoading(false); });
  }, []);

  const play = useCallback(() => startLive(), [startLive]);
  const pause = useCallback(() => { audioRef.current?.pause(); setPlaying(false); }, []);
  const toggle = useCallback(() => (playing ? pause() : play()), [playing, play, pause]);

  const available = !!config?.enabled && (config.mode === "external" ? !!config.external_url : (config.tracks || []).length > 0);

  return (
    <RadioCtx.Provider value={{ config, available, playing, loading, nowTitle, upNext, play, pause, toggle }}>
      {children}
    </RadioCtx.Provider>
  );
}
